import type { FireSnapshot } from '@elara/firebolt/FireCacheManager';
import type { AnyModelSpec, CollectionPathProps, ConstraintSpec, DocPathProps } from '@elara/firebolt/shared';
import type { TemplatePath } from '@elara/firebolt/TemplatePath';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type {
  ClientCollectionGroupQuery,
  ClientDocumentHandle,
  ClientModelInstance,
  MakeExtensions,
  ModelExtensions,
  ModelStatic,
} from '#firebase/types.js';

import { createFlatStore } from '@preact-signals/utils';
import { memoize } from 'lodash-es';

import { FirestoreCache } from '@elara/firebolt/FireCacheManager';

import { createClientDocument } from '#firebase/createClientDocument.js';
import { createClientInstance } from '#firebase/createClientInstance.js';
import { createClientQuery } from '#firebase/createClientQuery.js';

import { createClientDataConverter } from './createClientDataConverter.js';

export { memoize } from 'lodash-es';
export type * from 'zod';

/**
 * Creates a type-safe, reactive model class from a ModelSpec and optional extensions.
 *
 * @param firestore
 * @param spec - The ModelSpec defining the data structure and path.
 * @param makeExtensions - A function that creates the extensions for the model instance.
 * @param staticExtensions - An object of static extensions for the model.
 * @returns A model class with static methods for data access
 *
 * @example
 * ```typescript
 * // Assuming 'services' is an initialized FirebaseServiceContainer
 * const Chat = createClientModel(services, ChatSpec, () => ({
 *   // Computed property
 *   get participantCount() {
 *     return this.participants.length;
 *   },
 *
 *   // Method
 *   isUserParticipant(userId: string) {
 *     return this.participants.some(p => p.id === userId);
 *   },
 * }));
 *
 * // Usage
 * const chats = Chat.list({ userId: 'some-user-id' });
 * const chatHandle = Chat.get({ chatId: 'chatId' });
 * ```
 */

export function createClientModel<
  Spec extends AnyModelSpec,
  Extensions extends ModelExtensions<Spec>,
  StaticExtensions extends {},
>(
  firestore: FirestoreContainer,
  spec: Spec,
  makeExtensions: MakeExtensions<Spec, Extensions>,
  staticExtensions: StaticExtensions = {} as StaticExtensions,
  postProcessor?: (data: Spec['DataType']) => Spec['DataType'],
): ModelStatic<Spec, Extensions> & StaticExtensions {
  const { backend, collection, doc, getDoc, getDocs } = firestore;
  const converter = createClientDataConverter(spec, makeExtensions, postProcessor);
  const specPath = spec.path as TemplatePath;

  const model = {
    spec,
    converter,
    /**
     * Get a reference to the collection for this model.
     * @param pathProps - The path properties to construct the collection path.
     */
    collection(pathProps: CollectionPathProps<Spec>) {
      const collectionPath = specPath.toCollectionPath(pathProps);
      return collection(backend, collectionPath).withConverter(converter);
    },
    collectionGroup(collectionId: string) {
      const query = firestore.collectionGroup(backend, collectionId).withConverter(converter);
      Object.assign(query, { $id: collectionId });
      return query as ClientCollectionGroupQuery<Spec, Extensions>;
    },
    /**
     * Get a reference to a specific document in the collection.
     * @param pathProps - The path properties including the document ID.
     */
    doc(pathProps: DocPathProps<Spec>) {
      const documentPath = specPath.toDocumentPath(pathProps);
      return doc(backend, documentPath).withConverter(converter);
    },
    /**
     * Create a reactive query handle for the collection with optional constraints.
     * @param pathProps - The path properties to construct the collection path.
     * @param constraints - Any additional Firestore query constraints (e.g., `where`, `orderBy`).
     */
    list(pathProps: CollectionPathProps<Spec>, ...constraints: ConstraintSpec[]) {
      const coll = this.collection(pathProps);
      return createClientQuery(firestore, spec, coll, constraints);
    },
    /**
     * Create a reactive query handle for the collection with optional constraints.
     * @param pathProps - The path properties to construct the collection path.
     * @param constraints - Any additional Firestore query constraints (e.g., `where`, `orderBy`).
     */
    listCollectionGroup(id: string, ...constraints: ConstraintSpec[]) {
      const coll = this.collectionGroup(id);
      return createClientQuery(firestore, spec, coll, constraints);
    },
    /**
     * Get a single document by ID.
     * @param pathProps - The path properties including the document ID.
     */
    get(pathProps: DocPathProps<Spec>): ClientDocumentHandle<Spec, Extensions> {
      const docRef = this.doc(pathProps);
      return createClientDocument(firestore, spec, docRef);
    },
    /**
     * Check if a document exists.
     * @param pathProps - The record of path ids including the document id.
     */
    async exists(pathProps: DocPathProps<Spec>): Promise<boolean> {
      const docRef = this.doc(pathProps);
      const snapshot = await getDoc(docRef);
      return snapshot.exists();
    },
    /**
     * Get all documents in the collection.
     * @warning Only acceptable for small collections as it fetches all documents at once.
     * @param pathProps - The path properties to construct the collection path.
     */
    async all(pathProps: CollectionPathProps<Spec>): Promise<Array<ClientModelInstance<Spec, Extensions>>> {
      const collRef = this.collection(pathProps);
      const snapshot = await getDocs(collRef);
      return snapshot.docs.map((doc) => doc.data());
    },
    /**
     * Creates a client-side instance of a model from raw data, without hitting the database.
     * This is useful for creating temporary or optimistic instances.
     * @param pathProps - The path properties, including the document ID, to associate with the instance.
     * @param data - The raw input data for the model.
     */
    read(pathProps: DocPathProps<Spec>, data: Spec['InputType']): ClientModelInstance<Spec, Extensions> {
      const res = spec.schema.safeParse(data);
      if (!res.success) {
        console.warn(`Failed to read ${spec.name}(${pathProps.id}) from:`, { error: res.error, data });
        throw new Error(`Invalid data for ${spec.name}: ${res.error.message}`);
      }
      const docSnap: FireSnapshot<Spec['DataType']> = {
        id: pathProps.id,
        ref: this.doc(pathProps),
        data: memoize(() => spec.read(data)),
      };
      return FirestoreCache.acquire(docSnap, (snap) => createClientInstance(snap, spec.read(data), makeExtensions));
    },
  } as const satisfies ModelStatic<Spec, Extensions>;

  const [reactiveStaticExtensions] = createFlatStore(staticExtensions);
  Object.setPrototypeOf(model, reactiveStaticExtensions);
  return model as typeof model & StaticExtensions;
}
